import {TextField} from '@mui/material';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {TimePicker} from '@mui/x-date-pickers/TimePicker';
import moment from 'moment';
import 'moment/locale/de';
import React from 'react';
import {useTranslation} from 'react-i18next';

import {StyledDateTimeTextField} from './ETextInput';
import {FormInputWrapper} from './FormInputWrapper';
import {stopKeyboardEventPropagation} from './keyboardEventPropgation';
import {SharedProps} from './sharedProps';

const convertToHourMinTimeString = (value: moment.Moment | null) =>
  value && value.isValid() ? value.format('HH:mm') : '';

const convertToMoment = (value?: string) =>
  value ? moment(value, 'HH:mm') : null;

export type ETimeInputProps = SharedProps & {
  /** Time value in (HH:mm) format */
  value?: string;
  lang?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
};

export const ETimeInput = ({
  label,
  labelInfo,
  name,
  value,
  lang = 'de',
  bottomText,
  autoFocus,
  ariaLabel,
  disabled,
  onChange,
  onFocus,
  onBlur,
  error,
  errorMessage,
}: ETimeInputProps): React.ReactElement => {
  const {t} = useTranslation('lib');
  const localeTexts = {
    clearButtonLabel: t('actions.delete'),
  };
  const inputFormat = t('components.ETimeInput.inputFormat');

  // Emulate the native input event so that it can be seamlessly integrated into forms
  const onChangeHandler = (date: moment.Moment | null) => {
    const clonedEvent = new Event('input');
    Object.defineProperty(clonedEvent, 'target', {
      writable: true,
      value: {value: convertToHourMinTimeString(date), name},
    });
    onChange(clonedEvent as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  // Emulate the native input event so that it can be seamlessly integrated into forms
  const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur) {
      // Convert the onBlur value (localized time) to ISO time string
      e.target.value = e.target.value
        ? convertToHourMinTimeString(moment(e.target.value, inputFormat))
        : '';
      onBlur(e);
    }
  };

  return (
    <FormInputWrapper
      bottomText={bottomText}
      error={error}
      errorMessage={errorMessage}
      label={label}
      labelInfo={labelInfo}
      labelProps={{htmlFor: name}}
    >
      {/* note: this div is needed so that the end adornment has the correct height placement when bottomText or error exists */}
      <div style={{position: 'relative'}}>
        <LocalizationProvider
          adapterLocale={moment.locale(lang)}
          dateAdapter={AdapterMoment}
          localeText={localeTexts}
        >
          <TimePicker
            ampm={false}
            disabled={disabled}
            format={inputFormat}
            slotProps={{
              actionBar: {actions: ['clear']},
              textField: {
                id: name,
                name,
                autoFocus,
                disabled,
                error,
                onBlur: onBlurHandler,
                onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                  onFocus?.(e);
                },
                inputProps: {
                  'aria-label': ariaLabel,
                  'data-testid': `ETimeInput-${name}`,
                  ...(disabled ? {placeholder: ''} : {}),
                  onKeyDown: stopKeyboardEventPropagation,
                },
              },
            }}
            slots={{
              textField: StyledDateTimeTextField as typeof TextField,
            }}
            value={convertToMoment(value)}
            onChange={onChangeHandler}
          />
        </LocalizationProvider>
      </div>
    </FormInputWrapper>
  );
};
